'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Settings, Play, Save, Download, Users, TrendingUp, Award } from 'lucide-react';

export function JobEvaluation() {
  const [activeTab, setActiveTab] = useState('factors');

  const evaluationFactors = [
    {
      category: '인풋 (Input)',
      factors: [
        { id: 'knowledge', name: '지식', weight: 25, description: '직무 수행에 필요한 전문 지식' },
        { id: 'experience', name: '경험', weight: 20, description: '관련 업무 경험' },
        { id: 'environment', name: '작업환경', weight: 15, description: '물리적/정신적 작업 조건' }
      ]
    },
    {
      category: '프로세스 (Process)',
      factors: [
        { id: 'complexity', name: '업무 복잡성', weight: 30, description: '업무의 난이도와 복잡성' },
        { id: 'problem-solving', name: '문제해결', weight: 25, description: '문제 해결 능력 요구 수준' },
        { id: 'creativity', name: '창의성', weight: 20, description: '창의적 사고 요구 수준' }
      ]
    },
    {
      category: '아웃풋 (Output)',
      factors: [
        { id: 'responsibility', name: '책임도', weight: 35, description: '업무 결과에 대한 책임 수준' },
        { id: 'impact', name: '영향력', weight: 30, description: '조직에 미치는 영향' },
        { id: 'supervision', name: '지도감독', weight: 25, description: '타인에 대한 지도/감독 책임' }
      ]
    }
  ];

  const jobEvaluationResults = [
    { job: '프론트엔드 개발자', totalScore: 850, grade: 'L3', department: '개발부' },
    { job: '백엔드 개발자', totalScore: 920, grade: 'L4', department: '개발부' },
    { job: '영업 매니저', totalScore: 880, grade: 'L3', department: '영업부' },
    { job: '마케팅 스페셜리스트', totalScore: 790, grade: 'L2', department: '마케팅부' },
    { job: '인사 매니저', totalScore: 860, grade: 'L3', department: '인사부' },
    { job: 'UX 디자이너', totalScore: 820, grade: 'L3', department: '디자인부' }
  ];

  const gradeDistribution = [
    { grade: 'L1', count: 28, percentage: 15 },
    { grade: 'L2', count: 45, percentage: 24 },
    { grade: 'L3', count: 68, percentage: 36 },
    { grade: 'L4', count: 32, percentage: 17 },
    { grade: 'L5', count: 15, percentage: 8 }
  ];

  const evaluationProgress = [
    { month: '1월', completed: 45, total: 60 },
    { month: '2월', completed: 52, total: 65 },
    { month: '3월', completed: 68, total: 75 },
    { month: '4월', completed: 78, total: 85 },
    { month: '5월', completed: 89, total: 95 }
  ];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">직무평가</h1>
          <p className="text-xs sm:text-sm text-gray-600">체계적인 직무평가를 통한 공정한 등급 산정</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" className="text-xs sm:text-sm">
            <Download className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">평가 결과 </span>내보내기
          </Button>
          <Button size="sm" className="text-xs sm:text-sm">
            <Play className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">새 </span>평가<span className="hidden sm:inline"> 시작</span>
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="factors" className="text-[10px] sm:text-sm px-1 sm:px-3">평가 요소</TabsTrigger>
          <TabsTrigger value="evaluation" className="text-[10px] sm:text-sm px-1 sm:px-3">평가 진행</TabsTrigger>
          <TabsTrigger value="results" className="text-[10px] sm:text-sm px-1 sm:px-3">실시간 결과</TabsTrigger>
          <TabsTrigger value="analytics" className="text-[10px] sm:text-sm px-1 sm:px-3">분석</TabsTrigger>
        </TabsList>

        <TabsContent value="factors" className="space-y-4 sm:space-y-6">
          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>평가 요소 및 가중치 설정</span>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                직무평가에 사용할 요소들의 가중치를 설정합니다.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-6 sm:space-y-8">
                {evaluationFactors.map((category) => (
                  <div key={category.category} className="space-y-3 sm:space-y-4">
                    <h3 className="text-sm sm:text-lg font-semibold text-blue-600">{category.category}</h3>
                    <div className="grid gap-3 sm:gap-4">
                      {category.factors.map((factor) => (
                        <Card key={factor.id} className="p-3 sm:p-4">
                          <div className="flex items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2">
                            <div className="min-w-0">
                              <h4 className="font-medium text-xs sm:text-sm">{factor.name}</h4>
                              <p className="text-[10px] sm:text-sm text-gray-600 truncate">{factor.description}</p>
                            </div>
                            <Badge variant="outline" className="text-[10px] sm:text-xs flex-shrink-0">{factor.weight}점</Badge>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-[10px] sm:text-sm">
                              <span>가중치</span>
                              <span>{factor.weight}%</span>
                            </div>
                            <Slider
                              value={[factor.weight]}
                              max={50}
                              step={5}
                              className="w-full"
                            />
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evaluation" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">직무평가 진행</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">프론트엔드 개발자 직무 평가</CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 space-y-4 sm:space-y-6">
                  {evaluationFactors.map((category) => (
                    <div key={category.category} className="space-y-3 sm:space-y-4">
                      <h4 className="font-medium text-xs sm:text-sm text-blue-600">{category.category}</h4>
                      <div className="grid gap-3 sm:gap-4">
                        {category.factors.map((factor) => (
                          <div key={factor.id} className="p-3 sm:p-4 border rounded-lg space-y-2 sm:space-y-3">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-xs sm:text-sm">{factor.name}</span>
                              <span className="text-[10px] sm:text-sm text-gray-500 whitespace-nowrap">가중치: {factor.weight}%</span>
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-[10px] sm:text-sm">
                                <span>점수 (1-10)</span>
                                <span>8점</span>
                              </div>
                              <Slider
                                value={[8]}
                                max={10}
                                min={1}
                                step={1}
                                className="w-full"
                              />
                            </div>
                            <div className="text-[10px] sm:text-sm text-gray-600">
                              가중 점수: {(8 * factor.weight / 10).toFixed(1)}점
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end space-x-2 pt-2 sm:pt-4">
                    <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                      <Save className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                      임시저장
                    </Button>
                    <Button size="sm" className="text-xs sm:text-sm">평가 완료</Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">평가단 정보</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs sm:text-sm font-medium text-blue-600">김</span>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-xs sm:text-sm">김팀장</div>
                        <div className="text-[10px] sm:text-sm text-gray-500">개발팀</div>
                      </div>
                      <Badge className="ml-auto text-[10px] sm:text-xs">위원장</Badge>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs sm:text-sm font-medium text-green-600">박</span>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-xs sm:text-sm">박매니저</div>
                        <div className="text-[10px] sm:text-sm text-gray-500">인사부</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs sm:text-sm font-medium text-purple-600">이</span>
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-xs sm:text-sm">이차장</div>
                        <div className="text-[10px] sm:text-sm text-gray-500">전략기획</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-sm sm:text-base">실시간 점수</CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="text-center space-y-2">
                    <div className="text-2xl sm:text-3xl font-bold text-blue-600">850점</div>
                    <div className="text-xs sm:text-sm text-gray-500">현재 평가 점수</div>
                    <Progress value={85} className="w-full" />
                    <div className="text-xs sm:text-sm">예상 등급: <Badge className="text-[10px] sm:text-xs">L3</Badge></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="results" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">직무별 평가 결과</CardTitle>
                <CardDescription className="text-xs sm:text-sm">완료된 직무평가 결과 목록</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">직무명</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">부서</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">총점</TableHead>
                      <TableHead className="whitespace-nowrap text-xs sm:text-sm">등급</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobEvaluationResults.map((job, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium whitespace-nowrap text-xs sm:text-sm">{job.job}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs sm:text-sm hidden sm:table-cell">{job.department}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs sm:text-sm">{job.totalScore}점</TableCell>
                        <TableCell>
                          <Badge variant={job.grade === 'L5' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                            {job.grade}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-sm sm:text-base">등급별 분포</CardTitle>
                <CardDescription className="text-xs sm:text-sm">직무등급별 인원 분포 현황</CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={gradeDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="grade" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(value) => [value + '명', '인원수']} />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">평가 진행률</CardTitle>
              <CardDescription className="text-xs sm:text-sm">월별 직무평가 완료 현황</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={evaluationProgress}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    name="완료"
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#e5e7eb"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="목표"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">평가 완료율</CardTitle>
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">89.5%</div>
                <p className="text-[10px] sm:text-xs text-gray-500">
                  전월 대비 +12.3%
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">평균 평가 시간</CardTitle>
                <Users className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">2.4시간</div>
                <p className="text-[10px] sm:text-xs text-gray-500">
                  직무당 평균
                </p>
              </CardContent>
            </Card>

            <Card className="col-span-2 md:col-span-1">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-3 sm:p-6 pb-1 sm:pb-2">
                <CardTitle className="text-[10px] sm:text-sm whitespace-nowrap">평가단 만족도</CardTitle>
                <Award className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500 flex-shrink-0" />
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="text-lg sm:text-2xl font-bold">4.7/5.0</div>
                <p className="text-[10px] sm:text-xs text-gray-500">
                  프로세스 만족도
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="p-3 sm:p-6">
              <CardTitle className="text-sm sm:text-base">평가 품질 지표</CardTitle>
              <CardDescription className="text-xs sm:text-sm">평가의 일관성과 신뢰성 측정</CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 pt-0">
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-medium">평가자간 일치도</span>
                    <span className="text-xs sm:text-sm text-gray-500">92%</span>
                  </div>
                  <Progress value={92} className="w-full" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-medium">재평가 일관성</span>
                    <span className="text-xs sm:text-sm text-gray-500">88%</span>
                  </div>
                  <Progress value={88} className="w-full" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-medium">기준 준수율</span>
                    <span className="text-xs sm:text-sm text-gray-500">95%</span>
                  </div>
                  <Progress value={95} className="w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
